import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { LocalStorageService } from '@app-services/localStorage.service';
import { TourStep } from '@shared/components/tour/tour.types';

/**
 * Drives a single, app-wide guided tour. State is exposed as signals so the
 * shared `app-tour` overlay (rendered once at the app root) can react to it.
 *
 * Persisted "show only once" tours are keyed in `LocalStorageService`.
 */
@Injectable({ providedIn: 'root' })
export class TourService {
  private readonly localStorage = inject(LocalStorageService);

  private readonly _steps = signal<TourStep[]>([]);
  private readonly _currentIndex = signal<number>(-1);
  private pending: { key: string; value: string } | null = null;

  public readonly steps = this._steps.asReadonly();
  public readonly currentIndex = this._currentIndex.asReadonly();
  public readonly total = computed<number>(() => this._steps().length);
  public readonly isActive = computed<boolean>(() => this._currentIndex() >= 0 && this.total() > 0);
  public readonly currentStep = computed<TourStep | null>(() => this._steps()[this._currentIndex()] ?? null);
  public readonly isFirst = computed<boolean>(() => this._currentIndex() <= 0);
  public readonly isLast = computed<boolean>(() => this._currentIndex() === this.total() - 1);

  constructor() {
    inject(Router)
      .events.pipe(filter((e) => e instanceof NavigationStart))
      .subscribe(() => this.stop());
  }

  /** Starts the tour immediately (does not persist anything). */
  public start(steps?: TourStep[]): void {
    if (steps) this._steps.set(steps);
    if (!this._steps().length) return;
    this._currentIndex.set(0);
  }

  /**
   * Whether {@link startOnce} would start the tour for `key`/`version` right now.
   * Free of side effects, so callers can gate expensive work (layout reads,
   * observers) on it before committing to `startOnce`.
   */
  public canStartOnce(key: string, version?: string): boolean {
    if (this.isActive()) return false;
    return this.localStorage.getItem(key) !== (version ?? 'true');
  }

  /**
   * Starts the tour only if it has not been shown for the given `version` yet.
   * The "seen" flag is persisted only when the user dismisses the tour via
   * {@link dismiss} (Entendi/Pular) — not when it starts or stops for other
   * reasons. Pass a changing value (e.g. a WorkoutPlanId) to re-show the tour
   * whenever that value changes. Omit `version` for a plain show-once.
   *
   * @returns `true` if the tour was started, `false` if skipped (already seen
   * for this version or another tour is running).
   */
  public startOnce(key: string, steps?: TourStep[], version?: string): boolean {
    if (!this.canStartOnce(key, version)) return false;
    const expected = version ?? 'true';
    this.start(steps);
    if (!this.isActive()) return false;
    this.pending = { key, value: expected };
    return true;
  }

  /** Advances to the next step, dismissing the tour after the last one. */
  public next(): void {
    if (this.isLast()) {
      this.dismiss();
      return;
    }
    this._currentIndex.update((i) => i + 1);
  }

  public previous(): void {
    if (this.isFirst()) return;
    this._currentIndex.update((i) => i - 1);
  }

  /**
   * Dismisses the tour by user action (Entendi/Pular): persists the "seen" flag
   * for the active {@link startOnce} session, then ends the tour.
   */
  public dismiss(): void {
    if (this.pending) this.localStorage.setItem(this.pending.key, this.pending.value);
    this.stop();
  }

  /**
   * Ends the tour without persisting (navigation, host destroy, detached target).
   * Use {@link dismiss} when the user explicitly closes the tour.
   */
  public stop(): void {
    this.pending = null;
    this._currentIndex.set(-1);
  }
}
